import React, { useState } from 'react';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Background, Main, Shade } from 'styles/themeColors';
import styled from 'styled-components';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { GUIDE_TABS, TAB_LABELS, OPERATION_TYPES, ICON_TYPES, GUIDE_CONTENT } from 'constants/userGuide';

// ─── Tab Types ───────────────────────────────────────────────────────────────

type GuideTab = 'getting-started' | 'operations' | 'limitations';
type IconType = 'warning' | 'error' | 'info';

// ─── Styled Components ───────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${Shade.shade120};
  margin-bottom: 20px;
`;

const Tab = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? Main.brandDark : 'transparent')};
  margin-bottom: -2px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ active }) => (active ? Main.brandDark : Main.gray)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${Main.brandDark};
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${Main.black};
  margin: 0 0 8px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid ${Shade.shade120};
`;

const Paragraph = styled.p`
  font-size: 13px;
  color: ${Main.darkGray};
  line-height: 1.6;
  margin: 0 0 8px 0;
`;

const OperationBadge = styled.span<{ type: 'create' | 'update' | 'delete' }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 10px;
  background-color: ${({ type }) =>
    type === 'create' ? '#e6f4ea' :
    type === 'update' ? '#e8f0fe' :
    '#fce8e6'};
  color: ${({ type }) =>
    type === 'create' ? '#1e7e34' :
    type === 'update' ? '#1a56db' :
    '#c5221f'};
`;

const ExampleBox = styled.div`
  background-color: ${Background.shaded};
  border-left: 3px solid ${Main.brandDark};
  border-radius: 4px;
  padding: 10px 14px;
  margin: 6px 0;
  font-size: 13px;
  font-style: italic;
  color: ${Main.black};
  line-height: 1.5;
`;

const FieldTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 8px;
`;

const Th = styled.th`
  background-color: ${Background.shaded};
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  color: ${Main.black};
  border: 1px solid ${Shade.shade120};
`;

const Td = styled.td`
  padding: 8px 12px;
  color: ${Main.darkGray};
  border: 1px solid ${Shade.shade120};
  vertical-align: top;
`;

const LimitationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const LimitationIcon = styled.span<{ type: 'warning' | 'error' | 'info' }>`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 18px;
  color: ${({ type }) =>
    type === 'warning' ? '#f59e0b' :
    type === 'error' ? '#dc2626' :
    '#3b82f6'};

  svg {
    font-size: 18px;
  }
`;

const LimitationText = styled.div`
  font-size: 13px;
  color: ${Main.darkGray};
  line-height: 1.6;

  strong {
    color: ${Main.black};
  }
`;

const ScrollableContent = styled.div`
  max-height: 480px;
  overflow-y: auto;
  padding-right: 6px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${Shade.shade230};
    border-radius: 4px;
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const LIMITATION_ICON_MAP: Record<string, React.ReactNode> = {
  [ICON_TYPES.WARNING]: <WarningAmberOutlinedIcon />,
  [ICON_TYPES.ERROR]: <BlockOutlinedIcon />,
  [ICON_TYPES.INFO]: <InfoOutlinedIcon />,
};

interface LimitationEntry {
  type: string;
  bold: string;
  body: string;
}

function LimitationItems({ items }: { items: LimitationEntry[] }) {
  return (
    <>
      {items.map(item => (
        <LimitationItem key={item.bold}>
          <LimitationIcon type={item.type as IconType}>
            {LIMITATION_ICON_MAP[item.type]}
          </LimitationIcon>
          <LimitationText>
            <strong>{item.bold}</strong>{item.body}
          </LimitationText>
        </LimitationItem>
      ))}
    </>
  );
}

// ─── Tab Content Components ───────────────────────────────────────────────────

function GettingStartedTab() {
  const { WHAT_CAN_IT_DO, HOW_TO_USE, VOCABULARY, TIPS } = GUIDE_CONTENT.GETTING_STARTED;

  return (
    <ScrollableContent>
      <Section>
        <SectionTitle>{WHAT_CAN_IT_DO.TITLE}</SectionTitle>
        <Paragraph>{WHAT_CAN_IT_DO.PARA_1}</Paragraph>
        <Paragraph>
          {WHAT_CAN_IT_DO.PARA_2_SEGMENTS.map((seg, i) =>
            seg.bold ? <strong key={i}>{seg.text}</strong> : seg.text
          )}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>{HOW_TO_USE.TITLE}</SectionTitle>
        <Paragraph>{HOW_TO_USE.INTRO}</Paragraph>
        {HOW_TO_USE.EXAMPLES.map(ex => (
          <ExampleBox key={ex}>{ex}</ExampleBox>
        ))}
        <Paragraph style={{ marginTop: '12px' }}>{HOW_TO_USE.OUTRO}</Paragraph>
      </Section>

      <Section>
        <SectionTitle>{VOCABULARY.TITLE}</SectionTitle>
        <FieldTable>
          <thead>
            <tr>
              {VOCABULARY.TABLE_HEADERS.map(header => (
                <Th key={header}>{header}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {VOCABULARY.TABLE_ROWS.map(([input, output]) => (
              <tr key={input}>
                <Td>{input}</Td>
                <Td>{output}</Td>
              </tr>
            ))}
          </tbody>
        </FieldTable>
      </Section>

      <Section>
        <SectionTitle>{TIPS.TITLE}</SectionTitle>
        {TIPS.ITEMS.map(tip => (
          <LimitationItem key={tip.bold}>
            <LimitationIcon type={ICON_TYPES.INFO}><LightbulbOutlinedIcon /></LimitationIcon>
            <LimitationText>
              <strong>{tip.bold}</strong>{tip.body}
            </LimitationText>
          </LimitationItem>
        ))}
      </Section>
    </ScrollableContent>
  );
}

function OperationsTab() {
  const { CREATE, UPDATE, DELETE } = GUIDE_CONTENT.OPERATIONS;

  return (
    <ScrollableContent>
      <Section>
        <OperationBadge type={OPERATION_TYPES.CREATE}>{CREATE.BADGE}</OperationBadge>
        <SectionTitle>{CREATE.TITLE}</SectionTitle>
        <Paragraph>{CREATE.PARA_1}</Paragraph>
        {CREATE.EXAMPLES.map(ex => (
          <ExampleBox key={ex}>{ex}</ExampleBox>
        ))}
        <Paragraph style={{ marginTop: '10px' }}>{CREATE.PARA_2}</Paragraph>
      </Section>

      <Section>
        <OperationBadge type={OPERATION_TYPES.UPDATE}>{UPDATE.BADGE}</OperationBadge>
        <SectionTitle>{UPDATE.TITLE}</SectionTitle>
        <Paragraph>{UPDATE.PARA_1}</Paragraph>
        {UPDATE.EXAMPLES.map(ex => (
          <ExampleBox key={ex}>{ex}</ExampleBox>
        ))}
        <Paragraph style={{ marginTop: '10px' }}>{UPDATE.PARA_2}</Paragraph>
      </Section>

      <Section>
        <OperationBadge type={OPERATION_TYPES.DELETE}>{DELETE.BADGE}</OperationBadge>
        <SectionTitle>{DELETE.TITLE}</SectionTitle>
        <Paragraph>{DELETE.PARA_1}</Paragraph>
        {DELETE.EXAMPLES.map(ex => (
          <ExampleBox key={ex}>{ex}</ExampleBox>
        ))}
        <Paragraph style={{ marginTop: '10px' }}>{DELETE.PARA_2}</Paragraph>
      </Section>
    </ScrollableContent>
  );
}

function LimitationsTab() {
  const { VARIANT_COUNT, SKU_FORMATS, FIELDS, CLONING, CONDITIONS, INVALID_PROMPTS } = GUIDE_CONTENT.LIMITATIONS;

  return (
    <ScrollableContent>
      <Section>
        <SectionTitle>{VARIANT_COUNT.TITLE}</SectionTitle>
        <LimitationItems items={VARIANT_COUNT.ITEMS} />
      </Section>

      <Section>
        <SectionTitle>{SKU_FORMATS.TITLE}</SectionTitle>
        <FieldTable>
          <thead>
            <tr>
              {SKU_FORMATS.TABLE_HEADERS.map(header => (
                <Th key={header}>{header}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SKU_FORMATS.TABLE_ROWS.map(([style, example, works]) => (
              <tr key={style}>
                <Td>{style}</Td>
                <Td>{example}</Td>
                <Td>{works}</Td>
              </tr>
            ))}
          </tbody>
        </FieldTable>
      </Section>

      <Section>
        <SectionTitle>{FIELDS.TITLE}</SectionTitle>
        <LimitationItems items={FIELDS.ITEMS} />
      </Section>

      <Section>
        <SectionTitle>{CLONING.TITLE}</SectionTitle>
        <LimitationItems items={CLONING.ITEMS} />
      </Section>

      <Section>
        <SectionTitle>{CONDITIONS.TITLE}</SectionTitle>
        <LimitationItems items={CONDITIONS.ITEMS} />
      </Section>

      <Section>
        <SectionTitle>{INVALID_PROMPTS.TITLE}</SectionTitle>
        <LimitationItems items={INVALID_PROMPTS.ITEMS} />
      </Section>
    </ScrollableContent>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function UserGuide() {
  const [activeTab, setActiveTab] = useState<GuideTab>(GUIDE_TABS.GETTING_STARTED);

  return (
    <FlexboxContainer flexDirection="column">
      <TabBar>
        <Tab
          active={activeTab === GUIDE_TABS.GETTING_STARTED}
          onClick={() => setActiveTab(GUIDE_TABS.GETTING_STARTED)}
        >
          {TAB_LABELS.GETTING_STARTED}
        </Tab>
        <Tab
          active={activeTab === GUIDE_TABS.OPERATIONS}
          onClick={() => setActiveTab(GUIDE_TABS.OPERATIONS)}
        >
          {TAB_LABELS.OPERATIONS}
        </Tab>
        <Tab
          active={activeTab === GUIDE_TABS.LIMITATIONS}
          onClick={() => setActiveTab(GUIDE_TABS.LIMITATIONS)}
        >
          {TAB_LABELS.LIMITATIONS}
        </Tab>
      </TabBar>

      {activeTab === GUIDE_TABS.GETTING_STARTED && <GettingStartedTab />}
      {activeTab === GUIDE_TABS.OPERATIONS && <OperationsTab />}
      {activeTab === GUIDE_TABS.LIMITATIONS && <LimitationsTab />}
    </FlexboxContainer>
  );
}
